import React from "react";
import {
  ApolloClient,
  InMemoryCache,
  InMemoryCacheConfig,
  NormalizedCacheObject,
  ApolloProvider,
  DefaultOptions,
} from "@apollo/client";
import { withApollo as nextWithApollo } from "next-with-apollo";
import { getDataFromTree } from "@apollo/client/react/ssr";

type InitClientInput = {
  initialState: NormalizedCacheObject;
  uri: string;
  ssrOrigin?: string;
  cacheConfig?: InMemoryCacheConfig;
  defaultOptions?: DefaultOptions;
  connectToDevTools?: boolean;
};
export function initClient({
  initialState,
  uri,
  ssrOrigin,
  cacheConfig,
  defaultOptions,
  connectToDevTools,
}: InitClientInput) {
  return new ApolloClient({
    name: process.env.NEXT_PUBLIC_PACKAGE,
    version: process.env.NEXT_PUBLIC_PACKAGE_VERSION,
    uri,
    cache: new InMemoryCache(cacheConfig).restore(initialState),
    headers: {
      Accept: "application/json",
      ...(ssrOrigin ? { "x-forwarded-host": ssrOrigin } : undefined),
      ...(ssrOrigin ? { "x-ssr-origin": ssrOrigin } : undefined),
    },
    ...(defaultOptions && { defaultOptions }),
    connectToDevTools: !!connectToDevTools,
  });
}

type WithApolloInput = {
  uri: string;
  cacheConfig?: InMemoryCacheConfig;
  defaultOptions?: DefaultOptions;
  connectToDevTools?: boolean;
};

/**
 * Wraps a NextJS page with an Apollo provider and handles SSR data fetching
 */
export const withApollo = ({
  uri,
  cacheConfig,
  defaultOptions,
  connectToDevTools,
}: WithApolloInput) => {
  const finalUri = typeof window !== "undefined" ? "/api" : uri;
  return nextWithApollo(
    ({ initialState, ctx }) =>
      initClient({
        initialState,
        uri: finalUri,
        ssrOrigin:
          ctx?.req?.headers.host ||
          (ctx?.req?.headers["x-forwarded-host"] as string),
        cacheConfig,
        defaultOptions,
        connectToDevTools,
      }),
    {
      getDataFromTree,
      render: function NextWithApolloWrapper({ Page, props }) {
        return (
          <ApolloProvider client={props.apollo}>
            <Page {...props} />
          </ApolloProvider>
        );
      },
    }
  );
};
